import { FC, useCallback } from 'react';
import { useFragment } from 'react-relay';
import { OutputData } from '@editorjs/editorjs';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import EditorTranslateButton from 'components/EditorTranslateButton';
import EditorLanguageButton from 'components/EditorLanguageButton';

import { useEditor } from 'components/EditorJsField';

import {
  EditorToolbar_languages$key,
  Locale,
} from '__generated__/EditorToolbar_languages.graphql';
import { EditorToolbar_text$key } from '__generated__/EditorToolbar_text.graphql';
import { EditorToolbar_data$key } from '__generated__/EditorToolbar_data.graphql';
import { useFormikContext } from 'formik';

type Props = {
  languages: EditorToolbar_languages$key;
  text: EditorToolbar_text$key | null;
  data: EditorToolbar_data$key;
};

const EditorToolbar: FC<Props> = props => {
  const { name, selectedLocale } = useEditor();
  const { setFieldValue } = useFormikContext();

  const languages = useFragment(
    graphql`
      fragment EditorToolbar_languages on Language @relay(plural: true) {
        locale
        ...EditorLanguageButton_language
        ...EditorTranslateButton_to
        ...EditorTranslateButton_from
      }
    `,
    props.languages
  );

  const text = useFragment(
    graphql`
      fragment EditorToolbar_text on Text {
        blocks {
          edges {
            node {
              locale
              data

              ...EditorTranslateButton_textBlock
            }
          }
        }

        ...EditorLanguageButton_text
      }
    `,
    props.text
  );

  const data = useFragment(
    graphql`
      fragment EditorToolbar_data on Query {
        ...EditorTranslateButton_data
      }
    `,
    props.data
  );

  const handleTranslate = useCallback(
    (data: OutputData, locale: Locale) => {
      setFieldValue(`${name}.${locale}`, data);
    },
    [name, setFieldValue]
  );

  const language = languages.find(lang => lang.locale === selectedLocale);

  if (!language) return null;

  return (
    <Stack
      direction="row"
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
      sx={{ mr: 1 }}>
      <Stack direction="row" spacing={1}>
        {languages
          .filter(from => from.locale !== selectedLocale)
          .map(from => (
            <EditorTranslateButton
              key={from.locale}
              to={language}
              from={from}
              data={data}
              textBlock={
                text?.blocks?.edges.find(
                  ({ node }) => node.locale === from.locale
                )?.node ?? null
              }
              onTranslate={handleTranslate}
            />
          ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ pr: 1 }}>
        {languages.map(lang => (
          <EditorLanguageButton key={lang.locale} language={lang} text={text} />
        ))}

        <Divider flexItem orientation="vertical" sx={{ pl: 1 }} />
      </Stack>
    </Stack>
  );
};

export default EditorToolbar;
